import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import type { LibraryEntry } from './library-entries'
import { openTarget } from './open-target'
import { useCloudLibraryStore } from '../stores/cloud-library'

/**
 * 從書庫的一列開啟閱讀器。書庫頁與集合頁共用，兩邊的來源選擇規則因此不會分岔：
 * 本機可用且頁數與雲端相同就讀本機，否則讀雲端；頁數不符時交給來源選擇面板讓使用者決定。
 */
export function useEntryReader() {
  const router = useRouter()
  const toast = useToast()
  const cloud = useCloudLibraryStore()
  const choosingSource = ref<{ comic: Comic; cloudPageCount: number }>()

  async function read(entry: LibraryEntry) {
    const target = await openTarget(entry, id => cloud.detail(id))
    if (target.kind === 'blocked') {
      toast.warning('這本還沒有備份到雲端', { description: '在持有檔案的裝置備份完成後，其他裝置才能閱讀。' })
      return
    }
    if (target.kind === 'choose') {
      choosingSource.value = { comic: target.comic, cloudPageCount: target.cloudPageCount }
      return
    }
    if (target.notice) toast.warning(target.notice)
    await router.push(target.path)
  }

  async function chooseSource(source: 'local' | 'cloud') {
    const choice = choosingSource.value
    choosingSource.value = undefined
    if (!choice) return
    await router.push(source === 'cloud'
      ? `/read/cloud/${encodeURIComponent(choice.comic.cloudComicId!)}`
      : `/read/local/${encodeURIComponent(choice.comic.id)}`)
  }

  return { read, choosingSource, chooseSource }
}
